import { useSearchParams } from 'next/navigation';
import { useMemo, useRef } from 'react';

import type { TQuickFindLink } from '@/features/quickFindFilters/types';

import useGetRefinementPopoverProps from '@/components/ResultRefinements/RefinementPopover/useGetRefinementPopoverProps';
import { useUIResource } from '@/ducks/common/resources';
import { setFilters } from '@/ducks/filters/setters';
import { FILTER_KEY, type FiltersTypes } from '@/ducks/filters/types';
import { getQFSearchParams } from '@/features/quickFindFilters/helpers';
import { useAppSelector } from '@/store';
import { type NullablePartial } from '@/types/common';

import extractSelectedRegionInfo from './helpers/extractSelectedRegionInfo';
import makeDestinationsPillLabel from './helpers/makeDestinationsPillLabel';
import DestinationRefinement from './index';
import { selectDestinationsFilterData } from './selectors';
import { DestinationType, type TSelectedRegionInfo } from './types';

type UseRefinementFiltersProps = {
  id?: string;
  isMobile: boolean;
  onChangeStatus: (isOpen: boolean) => void;
};

const useDestinationRefinementPopover = ({ isMobile, onChangeStatus }: UseRefinementFiltersProps) => {
  const buttonRef = useRef<HTMLElement | null>(null);
  const searchParams = useSearchParams()!;
  const destinationsFilterData = useAppSelector(selectDestinationsFilterData);
  const emptyPillLabel = useUIResource('Destination.filter.text');

  const popoverProps = useGetRefinementPopoverProps({
    buttonRef,
    focusTrapDisableEvents: ['x-arrows', 'y-arrows'],
    isInline: isMobile,
    isMobile,
    onChangeStatus,
    outsidePress: !isMobile,
    placement: 'bottom-start',
  });

  const onComplete = (
    selectedRegionInfo: TSelectedRegionInfo,
    isResetVoyages?: boolean,
    isResetQF?: boolean,
    QFLink?: TQuickFindLink,
  ) => {
    const { destinationType, itemsIds, regionsIds } = selectedRegionInfo || {};
    const filters: NullablePartial<FiltersTypes> = {
      ...(isResetVoyages ? { [FILTER_KEY.voyageIds]: null } : undefined),
      ...(isResetQF
        ? [...getQFSearchParams(QFLink).keys()].reduce(
            (acc, key) => ({ ...acc, [key]: null }),
            {} as Record<string, string | null>,
          )
        : undefined),
      [FILTER_KEY.destPackages]: destinationType === DestinationType.ITINERARIES ? (itemsIds ?? []) : [],
      [FILTER_KEY.destPackagesRegions]: destinationType === DestinationType.ITINERARIES ? (regionsIds ?? []) : [],
      [FILTER_KEY.destPorts]: destinationType === DestinationType.PORTS_OF_CALL ? (itemsIds ?? []) : [],
      [FILTER_KEY.destPortsRegions]: destinationType === DestinationType.PORTS_OF_CALL ? (regionsIds ?? []) : [],
    };

    setFilters(filters);
  };

  const popover = useMemo(
    () => (
      <DestinationRefinement
        isMobile={isMobile}
        key="DestinationRefinementKey"
        onComplete={onComplete}
        popoverProps={popoverProps}
      />
    ),
    [isMobile, popoverProps],
  );

  const pillLabel = useMemo(() => {
    const selectedRegionInfo = extractSelectedRegionInfo(searchParams);
    return makeDestinationsPillLabel(destinationsFilterData, selectedRegionInfo) || emptyPillLabel;
  }, [searchParams, destinationsFilterData, emptyPillLabel]);

  const isLoading = useMemo(() => {
    const selectedRegionInfo = extractSelectedRegionInfo(searchParams);
    return selectedRegionInfo && !Object.values(destinationsFilterData).some((value) => value.length);
  }, [searchParams, destinationsFilterData]);

  return {
    ...popoverProps,
    buttonProps: popoverProps.getReferenceProps(/* { className: ... } */),
    isLoading,
    pillLabel,
    popover,
  };
};

export default useDestinationRefinementPopover;
